import { socialApiClient } from '@/shared/api-client/social-api-client';
import { exportToCSV } from '@/shared/utils/export-to-csv';
import type {
  AnalyzePromptResponseDTO,
  Creator,
  ExportCreatorListRequestDTO,
  FetchCreatorsResponseDTO,
  GetCreatorSearchStatusResponseDTO,
  LookalikeKeywordExtractionRequestDTO,
  LookalikeKeywordExtractionResponseDTO,
  SearchCreatorRequestDTO,
} from '.';
import {
  exportCreatorListRequestSchema,
  FetchCreatorByCacheIdRequestSchema,
  LookalikeKeywordExtractionRequestSchema,
  SearchCreatorRequestSchema,
} from '.';
import { MATCHED_TYPE } from '../public-filter';

export const AnalyzePrompt = async (
  workspace_id: string,
  prompt: string,
  signal?: AbortSignal,
): Promise<AnalyzePromptResponseDTO> => {
  const res = await socialApiClient.post<AnalyzePromptResponseDTO>('creators/analyze-prompt', {
    json: {
      workspace_id,
      message: prompt,
    },
    signal,
  });

  const result = await res.json();
  return result;
};

export const RequestSearchCreator = async (
  workspace_id: string,
  request: SearchCreatorRequestDTO,
  signal?: AbortSignal,
): Promise<{
  searchId: string;
}> => {
  const validatedFilter = SearchCreatorRequestSchema.parse(request);
  const response = await socialApiClient.post<FetchCreatorsResponseDTO>('creators/search', {
    json: {
      workspace_id,
      ...validatedFilter,
    },
    signal: signal,
  });
  const data = await response.json();

  return {
    searchId: data.search_id,
  };
};

export const RequestLookalikeKeywords = async (
  workspace_id: string,
  request: LookalikeKeywordExtractionRequestDTO,
  signal?: AbortSignal,
): Promise<LookalikeKeywordExtractionResponseDTO> => {
  const validatedRequest = LookalikeKeywordExtractionRequestSchema.parse(request);
  const response = await socialApiClient.post<LookalikeKeywordExtractionResponseDTO>(
    'creators/lookalike-suggestions',
    {
      json: {
        workspace_id,
        ...validatedRequest,
      },
      signal,
    },
  );

  return response.json();
};

export const RequestLookalikeSuggestions = RequestLookalikeKeywords;

export const GetCreatorSearchStatus = async (
  search_id: string,
  signal?: AbortSignal,
): Promise<GetCreatorSearchStatusResponseDTO> => {
  const response = await socialApiClient.get<GetCreatorSearchStatusResponseDTO>(
    `creators/search/${search_id}/status`,
    {
      signal: signal,
    },
  );
  const data = await response.json();
  return data;
};

export const FetchCreatorBySearchId = async (
  searchId: string,
  options: {
    attempt: number;
    showSaved?: boolean;
    matchedType?: MATCHED_TYPE;
  },
  signal?: AbortSignal,
): Promise<{
  creators: Array<Creator>;
  total_count: number;
  complete: boolean;
}> => {
  const params = FetchCreatorByCacheIdRequestSchema.parse({
    attempt: options.attempt,
    show_saved: options.showSaved,
    full_matched_only: options.matchedType === MATCHED_TYPE.FULL_MATCHED,
  });
  const response = await socialApiClient.get<{
    creators: Creator[];
    total_count: number;
    complete: boolean;
  }>(`creators/search/${searchId}/results`, {
    searchParams: params,
    signal,
  });

  const data = await response.json();

  return {
    creators: data.creators,
    total_count: data.total_count,
    complete: data.complete,
  };
};

export const ExportCreatorList = async (
  listId: string,
  fileName: string,
  params: ExportCreatorListRequestDTO,
  signal?: AbortSignal,
) => {
  const validatedParams = exportCreatorListRequestSchema.parse(params);
  const res = await socialApiClient.post(`creators/saved-lists/${listId}/export`, {
    json: validatedParams,
    signal,
  });
  const text = await res.text();
  exportToCSV(text, fileName);
};

export const ExportExcludedCreatorList = async (workspace_id: string, signal?: AbortSignal) => {
  const res = await socialApiClient.post(`creators/exclude-list/export`, {
    json: {
      workspace_id: workspace_id,
    },
    signal,
  });
  const text = await res.text();
  exportToCSV(text, 'exclude-list.csv');
};
